import React, { useEffect, useState } from 'react';
import GaugeChart from 'react-gauge-chart';

interface SurveyGaugeChartProps {
    data: any[];
    surveyJson: any;
}

const SurveyGaugeChart: React.FC<SurveyGaugeChartProps> = ({ data, surveyJson }) => {
    const [matrixResults, setMatrixResults] = useState<
        { name: string; title: string; average: number }[]
    >([]);

    useEffect(() => {
        const matrixQuestions = surveyJson.pages?.flatMap((page: any) =>
            page.elements.filter((el: any) => el.type === 'matrix')
        ) || [];

        const results = matrixQuestions.map((matrix: any) => {
            const name = matrix.name;
            const title = matrix.title?.tr || name;

            const columns = (matrix.columns || []).filter((col: any) => col?.value !== undefined);
            if (columns.length < 2) return { name, title, average: 0 };

            const columnValues = columns.map((col: any) => col.value);
            const isNumeric = columnValues.every((val: any) => !isNaN(parseFloat(val)));
            const minValue = isNumeric ? Math.min(...columnValues.map((v: any) => parseFloat(v))) : 0;
            const maxValue = isNumeric
                ? Math.max(...columnValues.map((v: any) => parseFloat(v)))
                : columns.length - 1;

            let total = 0;
            let count = 0;

            /*console.log(`\n▶ Hesaplama: ${name} - ${title}`);
            console.log(`Sütunlar: ${JSON.stringify(columnValues)}`);
            console.log(`Skala türü: ${isNumeric ? 'Sayısal' : 'Sıralı'} | Min: ${minValue} Max: ${maxValue}`);
            */
            data.forEach((entry, entryIndex) => {
                const answers = entry[name] as Record<string, string>;
                if (!answers) return;

                Object.entries(answers).forEach(([questionKey, answerVal]) => {
                    let numericScore: number | undefined;

                    if (isNumeric) {
                        const parsed = parseFloat(answerVal);

                        if (isNaN(parsed) || parsed === 0) {
                            //console.log(`⛔ Gözlem yok sayıldı: ${answerVal}`);
                            return; // hem katkı hem count dışı
                        }

                        // Katılmıyorum ise katkı 0, ama count’a dahil
                        if (parsed === 1) {
                            numericScore = 0;
                            count++;
                            //console.log(`🟡 Katılmıyorum (puan 0) → ${answerVal}`);
                            return;
                        }

                        numericScore = ((parsed - 1) / (maxValue - 1)) * 100;
                        count++;
                    } else {
                        const index = columnValues.indexOf(answerVal);
                        if (index <= 0) {
                            //console.log(`⛔ Cevap dışlandı (index=${index}): ${answerVal}`);
                            return;
                        }
                        numericScore = (index / maxValue) * 100;
                        count++;
                    }

                    //console.log(`✅ [Katkı] Katılımcı ${entryIndex + 1} | Soru ${questionKey} → "${answerVal}" → Puan: ${numericScore.toFixed(2)}`);
                    total += numericScore;
                });
            });

            const average = count > 0 ? total / count : 0;
            //console.log(`\nToplam Katkı: ${total.toFixed(2)}, Kişi Sayısı: ${count}, Ortalama: ${average.toFixed(2)}\n`);

            return { name, title, average };
        });


        const filteredResults = results.filter((r: any, i: number) => {
            const matrix = matrixQuestions[i];
            const rows = matrix?.rows?.filter(
                (row: any) => row && typeof row === 'object' && Object.keys(row).length > 0
            );
            return rows?.length > 0;
        });

        setMatrixResults(filteredResults);
    }, [data, surveyJson]);

    return (
        <div>
            <h2>Anket Sonuçları</h2>

            {/* Genel Ortalama */}
            {matrixResults.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                    <div style={{ width: '300px', textAlign: 'center' }}>
                        <h3>Genel Ortalama</h3>
                        <GaugeChart
                            id="gauge-overall"
                            nrOfLevels={3}
                            colors={['#FF5F6D', '#FFC371', '#4caf50']}
                            arcWidth={0.3}
                            percent={
                                matrixResults.reduce((sum, r) => sum + r.average, 0) /
                                matrixResults.length /
                                100
                            }
                            textColor="#4caf50"
                            formatTextValue={() =>
                                (
                                    matrixResults.reduce((sum, r) => sum + r.average, 0) /
                                    matrixResults.length
                                ).toFixed(1)
                            }
                        />
                    </div>
                </div>

            )}

            {/* Diğer sorular */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', justifyContent: 'center' }}>
                {matrixResults.map((res, index) => (
                    <div key={index} style={{ textAlign: 'center', flex: '0 1 300px' }}>
                        <h4>{res.title}</h4>
                        <GaugeChart
                            id={`gauge-${res.name}`}
                            nrOfLevels={3}
                            colors={['#FF5F6D', '#FFC371', '#4caf50']}
                            arcWidth={0.3}
                            percent={res.average / 100}
                            textColor="#4caf50"
                            formatTextValue={() => res.average.toFixed(1)}
                        />
                    </div>
                ))}
            </div>
        </div>
    );

};

export default SurveyGaugeChart;
